import { ChevronLeft, ChevronRight, Eye, FilterX, Pencil, Plus, Power, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ListCount,
  ListSurface,
  ListSurfaceBody,
  ListSurfaceFooter,
  ListSurfaceHeader,
  ListToolbar,
  ListToolbarGroup,
  SkeletonTableRow,
  ToolbarIconButton,
  WorkspacePage,
} from "@/components/ds";
import { ThemedSelect } from "@/components/ui-system/select";
import {
  deleteAnnouncement,
  listAnnouncements,
  setAnnouncementPublication,
  type Announcement,
} from "@/lib/api/endpoints/announcements";

const PAGE_SIZES = [10, 20, 50];

function audienceLabel(announcement: Announcement) {
  const targets = announcement.audiences;
  if (targets.some((target) => target.target_type === "all")) return "All users";
  if (targets.some((target) => target.target_type === "platform")) return "Platform users";
  if (targets.some((target) => target.target_type === "customers")) return "All customers";
  if (targets.some((target) => target.target_type === "workspace")) return "Selected workspace";
  if (targets.some((target) => target.target_type === "user")) return "Selected members";
  return "No audience";
}

export function AnnouncementsListPage() {
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [published, setPublished] = useState<"all" | "published" | "draft">("all");
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(0);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(0);
  }, [search, published, pageSize]);

  const offset = page * pageSize;
  const announcementsQuery = useQuery({
    queryKey: ["announcements", "management", search, published, pageSize, offset],
    queryFn: () => listAnnouncements("management", { search, published, limit: pageSize, offset }),
  });

  const publicationMutation = useMutation({
    mutationFn: ({ id, value }: { id: string; value: boolean }) => setAnnouncementPublication(id, value),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["announcements"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteAnnouncement,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["announcements"] });
    },
  });

  const items = announcementsQuery.data?.items ?? [];
  const total = announcementsQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const first = total === 0 ? 0 : offset + 1;
  const last = Math.min(offset + items.length, total);
  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setPublished("all");
  };

  return (
    <WorkspacePage>
      <ListSurface>
        <ListSurfaceHeader
          title="Announcements"
          meta={<ListCount total={total} noun={total === 1 ? "announcement" : "announcements"} />}
          controls={
            <ListToolbar>
              <ListToolbarGroup grow>
                <input
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search announcements"
                  className="ui-control h-10 w-full rounded-control bg-surface-primary pl-10 pr-3 text-caption"
                />
              </ListToolbarGroup>
              <ListToolbarGroup>
                <ThemedSelect
                  value={published}
                  onChange={(value) => setPublished(value as "all" | "published" | "draft")}
                  ariaLabel="Publication status"
                  className="w-44"
                  options={[
                    { value: "all", label: "Any publication" },
                    { value: "published", label: "Published" },
                    { value: "draft", label: "Draft" },
                  ]}
                />
              </ListToolbarGroup>
              {search || published !== "all" ? (
                <ToolbarIconButton icon={FilterX} label="Clear filters" onClick={clearFilters} />
              ) : null}
              <ListToolbarGroup>
                <Link to="/announcements/new" className="btn btn-primary">
                  <Plus className="mr-2 h-4 w-4" /> New announcement
                </Link>
              </ListToolbarGroup>
            </ListToolbar>
          }
        />
        <ListSurfaceBody className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[color:var(--table-border)] text-sm">
            <thead className="table-header-row bg-[var(--table-header-bg)] text-left text-[12px] font-semibold uppercase tracking-[0.08em] text-muted">
              <tr>
                <th className="px-5 py-3">Announcement</th>
                <th className="px-5 py-3">Audience</th>
                <th className="px-5 py-3">Published</th>
                <th className="px-5 py-3">Read</th>
                <th className="px-5 py-3">Created</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--table-border)]">
              {items.length ? (
                items.map((announcement, index) => {
                  const isPublished = announcement.status === "published";
                  return (
                    <tr
                      key={announcement.id}
                      className="table-data-row align-middle hover:bg-[var(--table-row-hover)]"
                      style={{ background: index % 2 ? "color-mix(in srgb, var(--muted-bg) 38%, transparent)" : "var(--bg-surface)" }}
                    >
                      <td className="px-5 py-4 font-medium text-foreground">
                        <Link to={`/announcements/${announcement.id}`} className="hover:text-primary hover:underline">
                          {announcement.title}
                        </Link>
                      </td>
                      <td className="px-5 py-4 text-muted">{audienceLabel(announcement)}</td>
                      <td className="px-5 py-4">
                        <button
                          type="button"
                          disabled={publicationMutation.isPending}
                          onClick={() => publicationMutation.mutate({ id: announcement.id, value: !isPublished })}
                          className={
                            isPublished
                              ? "inline-flex items-center gap-2 rounded-full bg-success/15 px-3 py-1 text-xs font-semibold text-success"
                              : "inline-flex items-center gap-2 rounded-full bg-muted-bg px-3 py-1 text-xs font-semibold text-muted"
                          }
                        >
                          <Power className="h-3.5 w-3.5" />
                          {isPublished ? "Published" : "Draft"}
                        </button>
                      </td>
                      <td className="px-5 py-4">
                        {announcement.read_summary ? (
                          <div>
                            <p className="font-medium text-foreground">
                              {announcement.read_summary.read_count} / {announcement.read_summary.recipient_count}
                            </p>
                            <p className="text-xs text-muted">{announcement.read_summary.read_rate}% read</p>
                          </div>
                        ) : <span className="text-muted">-</span>}
                      </td>
                      <td className="px-5 py-4 text-muted">{new Date(announcement.created_at).toLocaleString()}</td>
                      <td className="px-5 py-4">
                        <div className="flex justify-end gap-2">
                          <Link
                            to={`/announcements/${announcement.id}`}
                            title="View announcement"
                            className="inline-flex h-9 w-9 items-center justify-center rounded-control border border-card-border text-muted transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary"
                          >
                            <Eye className="h-4 w-4" />
                          </Link>
                          <Link
                            to={`/announcements/${announcement.id}/edit`}
                            title="Edit announcement"
                            className="inline-flex h-9 w-9 items-center justify-center rounded-control border border-primary/15 bg-primary/10 text-primary transition hover:bg-primary/15"
                          >
                            <Pencil className="h-4 w-4" />
                          </Link>
                          <button
                            type="button"
                            title="Delete announcement"
                            disabled={deleteMutation.isPending}
                            onClick={() => {
                              if (window.confirm(`Delete "${announcement.title}"? This cannot be undone.`)) {
                                deleteMutation.mutate(announcement.id);
                              }
                            }}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-control border border-error/20 bg-error/10 text-error transition hover:bg-error/15"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : announcementsQuery.isLoading ? (
                <tr>
                  <td className="px-5 py-4" colSpan={6}>
                    {Array.from({ length: 6 }, (_, index) => <SkeletonTableRow key={index} cols={6} />)}
                  </td>
                </tr>
              ) : (
                <tr>
                  <td className="px-5 py-10 text-center text-muted" colSpan={6}>
                    No announcements match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </ListSurfaceBody>
        <ListSurfaceFooter
          left={
            <label className="flex items-center gap-2 text-sm text-muted">
              Rows per page
              <ThemedSelect
                value={String(pageSize)}
                onChange={(value) => setPageSize(Number(value))}
                ariaLabel="Rows per page"
                className="w-24"
                options={PAGE_SIZES.map((size) => ({ value: String(size), label: String(size) }))}
              />
            </label>
          }
          right={
            <>
              <span className="text-sm text-muted">{total ? `Showing ${first}-${last} of ${total}` : "No announcements"}</span>
              <div className="flex items-center gap-2">
                <button type="button" className="btn btn-secondary btn-sm" disabled={page === 0} onClick={() => setPage((value) => value - 1)}>
                  <ChevronLeft className="h-4 w-4" /> Previous
                </button>
                <span className="text-sm text-muted">Page {page + 1} / {pageCount}</span>
                <button type="button" className="btn btn-secondary btn-sm" disabled={page + 1 >= pageCount} onClick={() => setPage((value) => value + 1)}>
                  Next <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </>
          }
        />
      </ListSurface>
    </WorkspacePage>
  );
}
